import { useEffect, useState } from 'react';
import { Box, useTheme, alpha } from '@mui/material';
import { keyframes } from '@mui/system';

// A crisp ~1 Hz blink for the colon, the way a real digital clock ticks.
const colonBlink = keyframes`
  0%, 49.9% { opacity: 1; }
  50%, 100% { opacity: 0.18; }
`;

// Monospace + tabular figures so the digits sit on a fixed seven-segment grid.
const SEG_FONT =
  "ui-monospace, 'SFMono-Regular', 'Roboto Mono', Menlo, Consolas, monospace";

/**
 * A seven-segment-style digital clock. It owns its own 1-second tick (so only
 * this tiny component re-renders each second, not the whole nav). The look is
 * faked without a special font: a faint "88:88" ghost layer sits behind the
 * live time so the unlit segments read like an LCD/LED panel, and the colon
 * blinks once per second.
 */
const DigitalClock = () => {
  const theme = useTheme();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const led = theme.palette.primary.main; // brand amber/orange
  const isDark = theme.palette.mode === 'dark';

  let h = now.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const hh = String(h).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
      <Box
        aria-label={`${hh}:${mm} ${ampm}`}
        sx={{
          display: 'inline-grid',
          px: 0.75,
          py: '1px',
          borderRadius: '6px',
          backgroundColor: alpha(led, isDark ? 0.14 : 0.1),
          border: `1px solid ${alpha(led, 0.22)}`,
          fontFamily: SEG_FONT,
          fontWeight: 700,
          fontSize: '13px',
          letterSpacing: '2px',
          lineHeight: 1.5,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {/* Unlit ghost segments */}
        <Box
          component='span'
          aria-hidden
          sx={{ gridArea: '1 / 1', color: alpha(led, 0.16), pr: '2px' }}
        >
          88:88
        </Box>
        {/* Live time */}
        <Box
          component='span'
          sx={{
            gridArea: '1 / 1',
            color: led,
            textShadow: `0 0 6px ${alpha(led, 0.5)}`,
            pr: '2px',
          }}
        >
          {hh}
          <Box
            component='span'
            sx={{
              animation: `${colonBlink} 1s linear infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            :
          </Box>
          {mm}
        </Box>
      </Box>
      <Box
        component='span'
        sx={{
          fontFamily: SEG_FONT,
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '1px',
          color: 'text.secondary',
        }}
      >
        {ampm}
      </Box>
    </Box>
  );
};

export default DigitalClock;
